import * as React from 'react';
import { browserHistory } from 'react-router';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import {
	getArticles,
	deleteArticle,
	setArticlesSortFilter,
	resetArticlesData,
	resetArticlesFilters,
} from 'modules/magazine/article/articles';
import { IArticlesAction, IArticlesData } from 'models/magazine/article/articles';
import { IItemCardList } from 'models/itemcard/itemcardlist';
import * as c from 'models/constants';
import { IInfiniteScrollProps } from 'models/infinitescroll';
import { IUserPreferences } from 'models/user/userpreferences';
import { IUserCardData } from 'models/user/usercard';
import { ItemCardList, AddEntityItemCard } from 'components';
import { ErrorPage } from 'containers';
import { showModal, closeModal } from 'modules/modal';
import { IModalAction } from 'models/modal';
import { UserArticlesSort } from 'models/enums/sortvalues';
import { IDropdownData } from 'models/common';
import { getArticleCard } from 'redux/modules/magazine/article/articlecard';
import { IArticleCardAction } from 'models/magazine/article/articlecard';

interface IProps {
	articlesData?: IArticlesData;
	userPreferences?: IUserPreferences;
	userCard?: IUserCardData;
	selectedTab?: string;

	getArticles?: (id: string, isFirstLoad: boolean) => IArticlesAction;
	deleteArticle?: (itemId: string) => IArticlesAction;
	setArticlesSortFilter?: (sort: number) => IArticlesAction;
	resetArticlesData?: () => IArticlesAction;
	resetArticlesFilters?: () => IArticlesAction;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	t?: (key: string) => string;
	getArticleCard?: (id: string) => IArticleCardAction;
	dispatch?: any;
}

@connect(
	(state: IStore) => ({
		articlesData: state.articles,
		userPreferences: state.userPreferences,
		userCard: state.usercard,
	}),
	(dispatch) => ({
		getArticles: (userId: string, isFirstLoad: boolean) => dispatch(getArticles(userId, isFirstLoad)),
		deleteArticle: (itemId: string) => dispatch(deleteArticle(itemId)),
		setArticlesSortFilter: (sort: number) => dispatch(setArticlesSortFilter(sort)),
		resetArticlesFilters: () => dispatch(resetArticlesFilters()),
		resetArticlesData: () => dispatch(resetArticlesData()),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
		getArticleCard: (id: string) => dispatch(getArticleCard(id)),
		dispatch,
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class Articles extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.loadData = this.loadData.bind(this);
		this.deleteArticle = this.deleteArticle.bind(this);
		this.openDeleteArticle = this.openDeleteArticle.bind(this);
		this.handleOnSortClick = this.handleOnSortClick.bind(this);
	}

	componentDidMount(): void {
		this.loadData(true);
	}

	componentDidUpdate(prevProps: IProps) {
		if (prevProps.userCard.usercard?.id !== this.props.userCard.usercard?.id) {
			this.loadData(true);
		}
	}

	private loadData(firstLoad?: boolean) {
		if (this.props.userCard.usercard?.id) {
			this.props.getArticles(this.props.userCard.usercard.id, Boolean(firstLoad));
		}
	}

	public componentWillUnmount() {
		this.props.resetArticlesData();
		this.props.resetArticlesFilters();
	}

	private handleOnSortClick(value: any) {
		this.props.setArticlesSortFilter(parseInt(value, 10));
		this.props.getArticles(this.props.userCard.usercard.id, true);
	}

	private openDeleteArticle(_, itemId: string) {
		const { t } = this.props;

		const dialogContent = (
			<div className="confirmationDialogModal__contentContainer">
				<img src={require('containers/Html/icons/icon_notification.png')} />
				<h2>{t('Magazine_AdminArticles_DeleteModal_Title')}</h2>
				<p>{t('Magazine_AdminArticles_DeleteModal_Label_ActionIsIrreversible')}</p>
				<ul>
					<li>{t('Magazine_AdminArticles_DeleteModal_Label_NoLongerAccessible')}</li>
					<li>{t('Magazine_AdminArticles_DeleteModal_Label_AnyComments')}</li>
					<li>{t('Magazine_AdminArticles_DeleteModal_Label_Links')}</li>
				</ul>
			</div>
		);

		this.props.showModal('ConfirmationDialog', {
			confirmAction: this.deleteArticle,
			value: itemId,
			yesButtonText: this.props.t('BSPortal_Common_YesDelete'),
			noButtonText: this.props.t('BSPortal_Common_NoWait'),
			content: dialogContent,
			closeModal: this.props.closeModal,
		});
	}

	private deleteArticle(confirm: boolean, itemId: string) {
		if (confirm) {
			this.props.deleteArticle(itemId);
		}
		this.props.closeModal();
	}

	private handleCreateArticleClick() {
		browserHistory.push('/newarticle');
	}

	public render() {
		if (this.props.articlesData.error) {
			return <ErrorPage {...this.props} message={this.props.articlesData.message} />;
		}

		const { items, itemsPage, isFetching } = this.props.articlesData;
		const { isMyProfile } = this.props.userCard;
		const { t } = this.props;

		let articles = [];

		if (this.props.articlesData !== undefined || this.props.articlesData !== null) {
			articles = this.props.articlesData.items;
		}

		const infiniteScrollProps: IInfiniteScrollProps = {
			dataLength: items.length,
			next: this.loadData,
			hasMore: itemsPage.totalItems > items.length,
			loader: <h4>{t('BSPortal_Common_Loading')} </h4>,
		};

		const listProps: IItemCardList = {
			items: articles,
			itemType: c.NODE_TYPE_ARTICLE_ITEM_CARD,
			isInfiniteScroll: true,
			infiniteScrollProps,
			currentUserId: this.props.userPreferences.id,
			currentUserIsAdministrator: this.props.userPreferences.isAdministrator,
			currentUserIsEditor: this.props.userPreferences.isEditor,
			editItem: (id: string) => {
				browserHistory.push('/managearticle/' + id);
			},
			deleteItem: this.openDeleteArticle,
			isFetching,
			loadingText: t('BSPortal_Common_Loading'),
			goToCard: (id: string) => this.props.getArticleCard(id),
		};

		if (isMyProfile) {
			listProps.addItemCard = (
				<AddEntityItemCard action={this.handleCreateArticleClick} text={t('Magazine_Article_Articles_AddArticle')} />
			);
		}

		const sortItems: IDropdownData[] = [];
		for (const n in UserArticlesSort) {
			if (typeof UserArticlesSort[n] === 'number') {
				sortItems.push({ key: UserArticlesSort[n], value: t('User_UserCard_Articles_Sort_' + n) });
			}
		}

		return (
			<div className="userArticlesContainerWrapper contentCards">
				<ItemCardList {...listProps} />
			</div>
		);
	}
}
export { Articles };
